/**
 * useStableSearchParams - A bulletproof wrapper around react-router's useSearchParams
 * 
 * PROBLEM THIS SOLVES:
 * React Router's useSearchParams returns a new URLSearchParams object reference on every
 * URL change. When used in useEffect dependencies, this can cause infinite loops:
 * 
 *   useEffect(() => {
 *     // Read from searchParams
 *     const tab = searchParams.get('tab');
 *     // Write to searchParams (BAD - triggers the effect again!)
 *     setSearchParams({ tab: 'new' });
 *   }, [searchParams]); // searchParams changes on every setSearchParams call
 * 
 * SOLUTION:
 * This hook provides:
 * 1. Stable getter functions that don't trigger re-renders
 * 2. Batched/debounced setters that prevent rapid successive updates
 * 3. A "read-once" pattern for initial URL parsing
 * 4. Safe dependencies that can be used in useEffect without causing loops
 * 
 * USAGE:
 *   const { getParam, setParam, setParams, initialParams } = useStableSearchParams();
 *   
 *   // Read initial values (only on mount)
 *   useEffect(() => {
 *     const tab = initialParams.tab;
 *     if (tab) setActiveTab(tab);
 *   }, []); // Empty deps - initialParams is stable
 *   
 *   // Update URL safely
 *   const handleTabChange = (newTab) => {
 *     setActiveTab(newTab);
 *     setParam('tab', newTab);
 *   };
 */

import { useSearchParams } from 'react-router-dom';
import { useRef, useCallback, useMemo, useEffect, useState } from 'react';

/**
 * Capture initial search params on mount - this is safe because it only
 * runs once during the initial render before any effects
 */
const captureInitialParams = (searchParams) => {
  return Object.fromEntries(searchParams.entries());
};

/**
 * Stable search params hook that prevents infinite loops
 */
export const useStableSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Store current params in a ref - this allows reading without triggering re-renders
  const paramsRef = useRef(searchParams);
  
  // Keep the ref updated (but don't cause re-renders)
  useEffect(() => {
    paramsRef.current = searchParams;
  });
  
  // Capture initial params using useState initializer (runs once, safe for SSR)
  const [initialParams] = useState(() => captureInitialParams(searchParams));
  
  /**
   * Get a single param value (stable function, safe for any use)
   * Uses ref so it always gets the current value without re-render dependency
   */
  const getParam = useCallback((key) => {
    return paramsRef.current.get(key);
  }, []);
  
  /**
   * Get all current params as an object (stable function)
   */
  const getAllParams = useCallback(() => {
    return Object.fromEntries(paramsRef.current.entries());
  }, []);
  
  /**
   * Check if a param exists (stable function)
   */
  const hasParam = useCallback((key) => {
    return paramsRef.current.has(key);
  }, []);
  
  // Pending updates accumulator for batching
  const pendingUpdatesRef = useRef({});
  const updateTimeoutRef = useRef(null);
  
  /**
   * Flush pending updates to the URL
   * Uses requestAnimationFrame + setTimeout for optimal batching
   */
  const flushUpdates = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      const updates = pendingUpdatesRef.current;
      if (Object.keys(updates).length === 0) return;
      
      // Build new params from current + pending updates
      const newParams = new URLSearchParams(paramsRef.current);
      
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      
      // Clear pending updates
      pendingUpdatesRef.current = {};
      
      // Update URL with replace to avoid history spam
      setSearchParams(newParams, { replace: true });
    }, 0); // Microtask timing - batches synchronous calls
  }, [setSearchParams]);
  
  /**
   * Set a single param (batched, safe to call multiple times)
   */
  const setParam = useCallback((key, value) => {
    pendingUpdatesRef.current[key] = value;
    flushUpdates();
  }, [flushUpdates]);
  
  /**
   * Set multiple params at once (batched)
   */
  const setParams = useCallback((params) => {
    Object.assign(pendingUpdatesRef.current, params);
    flushUpdates();
  }, [flushUpdates]);
  
  /**
   * Remove a param from the URL
   */
  const removeParam = useCallback((key) => {
    pendingUpdatesRef.current[key] = null;
    flushUpdates();
  }, [flushUpdates]);
  
  /**
   * Remove multiple params
   */
  const removeParams = useCallback((keys) => {
    keys.forEach(key => {
      pendingUpdatesRef.current[key] = null;
    });
    flushUpdates();
  }, [flushUpdates]);
  
  /**
   * Replace all params (clear existing, set new)
   */
  const replaceAllParams = useCallback((params) => {
    // Get current params to know what to clear
    const currentKeys = Array.from(paramsRef.current.keys());
    
    // Clear all existing by setting to null
    currentKeys.forEach(key => {
      if (!(key in params)) {
        pendingUpdatesRef.current[key] = null;
      }
    });
    // Set new values
    Object.assign(pendingUpdatesRef.current, params);
    flushUpdates();
  }, [flushUpdates]);
  
  /**
   * Current params as an object (triggers re-render on URL change)
   * Use this ONLY when you need reactive updates to URL changes
   */
  const currentParams = useMemo(() => {
    return Object.fromEntries(searchParams.entries());
  }, [searchParams]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);
  
  return {
    // Stable getters (safe for any use, no re-render triggers)
    getParam,
    getAllParams,
    hasParam,
    
    // Stable initial values (captured on mount, never changes)
    initialParams,
    
    // Batched setters (safe to call multiple times, won't cause loops)
    setParam,
    setParams,
    removeParam,
    removeParams,
    replaceAllParams,
    
    // Reactive current params (use sparingly - can trigger re-renders)
    currentParams,
    
    // Raw access for edge cases (use with caution!)
    _rawSearchParams: searchParams,
    _rawSetSearchParams: setSearchParams,
  };
};

/**
 * Hook for components that only need to READ initial URL params once
 * This is the safest option for most use cases
 */
export const useInitialSearchParams = () => {
  const [searchParams] = useSearchParams();
  
  // Capture on first render only using useState initializer
  const [initialParams] = useState(() => captureInitialParams(searchParams));
  
  return initialParams;
};

/**
 * Hook for URL state that syncs bidirectionally with React state
 * Prevents loops by comparing values before updating
 */
export const useUrlState = (key, defaultValue = '') => {
  const { getParam, setParam, initialParams } = useStableSearchParams();
  
  // Initialize from URL or default
  const [value, setValue] = useState(() => {
    return initialParams[key] ?? defaultValue;
  });
  
  // Ref to track if we initiated the change
  const isInternalUpdate = useRef(false);
  
  // Sync URL when value changes (but not if URL initiated the change)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    
    const urlValue = getParam(key);
    const stateValue = value === defaultValue ? null : value;
    
    if (urlValue !== stateValue) {
      setParam(key, stateValue);
    }
  }, [value, key, defaultValue, getParam, setParam]);
  
  // Custom setter that also updates URL
  const setValueAndUrl = useCallback((newValue) => {
    setValue(newValue);
  }, []);
  
  return [value, setValueAndUrl];
};

export default useStableSearchParams;
